/**
 * Mock 数据层。
 * 目标：在没有后端的情况下，把「任务提交 → 排队 → 进度 → 出图 → 落库」整条链路
 * 完整跑通，且行为（状态机、进度公式、耗时档位）与参考站一致。
 * 换真实后端时，只要 VITE_API_MODE=http，本文件完全不会被打包执行。
 */
import type {
  AdminLoginResponse,
  AdminOverview,
  AdminUser,
  ApiClient,
  AssetItem,
  AssetLibraryItem,
  AssetLibraryQuery,
  AuthUser,
  CoinLedgerItem,
  CreateAdminRequest,
  CreateUserRequest,
  CreateVideoTemplateRequest,
  CustomVideoTemplate,
  AnalyzeVideoRequest,
  AnalyzeVideoResponse,
  FillFormRequest,
  FillFormResponse,
  GenerateRequest,
  Identity,
  JobOutput,
  JobState,
  LoginResponse,
  ModelFamilyInfo,
  PollResponse,
  PricePreviewRequest,
  PricePreviewResponse,
  ProductAsset,
  ProductAssetFacets,
  ProductAssetImage,
  ProductAssetQuery,
  CreateProductAssetRequest,
  UpdateProductAssetRequest,
  BulkCreateProductAssetsResponse,
  ProductCategory,
  RenameProductCategoryResponse,
  RefinePromptRequest,
  RefinePromptResponse,
  RunResponse,
  ServerJobItem,
  SkillIndex,
  UpdateAdminRequest,
  UpdateUserRequest,
  UpdateVideoTemplateRequest,
  UploadResult,
  UpstreamCatalog,
  UpstreamCatalogResult,
  UpstreamCreate,
  UpstreamFamily,
  UpstreamGroup,
  UpstreamItem,
  UpstreamUpdate,
  UsageSummary,
  VoiceCatalog,
  VoicePreviewRequest,
  VoicePreviewResponse,
  WorkItem,
} from './types'
/* 值导入（不是 type）：mock 的返回值要按场景给键，清单与真实前端共用一份 */
import { FILL_FORM_FIELDS } from './types'
/* 值导入：mock 里「做不了的事」要抛与真实 client 同一种错误（界面按 message 显示） */
import { ApiError } from './client'
import { progressFrom } from '@/config/catalog'
import { FALLBACK_FAMILIES, DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DEFAULT_VOICE_ID, FALLBACK_VOICE_GROUPS } from '@/config/voices'
import { classifyLocal, FALLBACK_CATEGORIES, FALLBACK_ORIGINS } from '@/config/assetTaxonomy'

/* ------------------------------- 内部状态 ------------------------------- */

type MockJob = {
  jobId: string
  appSlug: string
  title: string
  sourcePage: string
  model: string
  prompt: string
  createdAt: number
  estimatedMs: number
  outputs: number
  aspectRatio: string
  /** 逐张尺寸：与真实后端一致，第 i 张用 ratios[i]（缺位回退 aspectRatio） */
  ratios?: string[]
  /**
   * 批次 id。mock 也照存 —— 不然切到 mock 模式时「一批 N 行」会被渲染成
   * N 张互不相干的卡，而真实后端下是聚成一张的：**同一个界面两套分组行为**，
   * 是最难查的那种不一致（改了接口在 mock 下永远看不出问题）。
   */
  batchId?: string
  textResult?: string
  forceFail?: boolean
}

const jobs = new Map<string, MockJob>()
const worksDb: WorkItem[] = []
const assetsDb: AssetItem[] = []
/**
 * 用户自存的视频模板（mock 版，2026-10-08）。
 *
 * 进程内数组而不是 localStorage：真实后端是按**账号**收口的，
 * mock 里再存一份到浏览器，就等于给「换账号看到别人的模板」造了个假现场。
 * 刷新即失反而是对的 —— mock 模式的定位是跑通链路，不是当数据源。
 */
const videoTemplatesDb: CustomVideoTemplate[] = []

/**
 * mock 的**主账号 id**（`songshuai@hsy.com`）。
 *
 * 🔴 **为什么声明在这个位置、而不是紧挨着下面的 `mockUsers`**：
 * 商品种子在**模块初始化时**（`seedProductAssets()` 是个顶层调用）就要拿它
 * 登记分类，而 `mockUsers`（文件下部）与 `mockSessionUserId`（更下面）**都还没初始化**。
 * 种子去读它们不会得到空值，而是直接命中 **TDZ**，症状是**整个 app 白屏**，
 * 报错还指在一条与商品毫无关系的行上：
 *
 *     ReferenceError: Cannot access 'mockSessionUserId' before initialization
 *         at currentMockUserId (mock.ts)
 *         at mockEnsureProductCategory (mock.ts)   ← 种子在调它
 *
 * （2026-10-09 实测，白屏就是这个原因。）
 *
 * 所以这个 id **只许有一份**：`mockUsers[0]`、种子商品的 `userId`、
 * 种子分类的归属三处共用它。任何一处自己再写一遍字面量，
 * 都会在某次排序调整后悄悄把数据挂到另一个账号名下。
 */
const MOCK_OWNER_ID = 'u-mock-owner'

/**
 * 商品资产（mock 版，2026-10-08）。
 *
 * 与 `videoTemplatesDb` 一样是**进程内数组**，理由同样是不给「换账号看到
 * 别人的数据」造假现场。但它**预置了两条示例**：这一页的价值全在
 * 「列表里有东西、详情页能引用」，空列表进去看不出任何东西 ——
 * 而 mock 模式的定位正是「把链路演示出来」。
 */
const productAssetsDb: ProductAsset[] = []

/**
 * 商品分类（mock 版）—— 与 `productAssetsDb` 同样是进程内数组。
 *
 * ⚠️ 这里**不预置分类**，而是让两条种子商品各自带一个分类、由
 * `mockEnsureProductCategory` 在种的时候登记进来 —— 抄的是真实后端
 * 「分类名就是商品上的那个字符串，写商品时顺带把名字登记进分类表」那条口径。
 * 手写一份预置分类清单是**第二份真源**：改了种子商品的分类，
 * 分类表不会跟着变，于是 mock 下出现「分类条上有个空分类、
 * 而商品的分类名不在条上」这种真后端不可能有的状态。
 */
const productCategoriesDb: ProductCategory[] = []

/** 未分类商品在 mock 里也要能被筛出来 —— 与后端 `UNCATEGORIZED_CATEGORY` 同一个哨兵 */
const MOCK_UNCATEGORIZED = '__uncategorized__'

/**
 * 把一个分类名登记进 mock 分类表，返回表里的写法（大小写不敏感，抄后端 `ensure_product_category`）。
 *
 * `ownerId` 允许显式传入，**这不是为了方便**：种子里登分类时，
 * `currentMockUserId()` 还取不到值（`mockSessionUserId` / `mockUsers`
 * 都声明在文件下面，读了就是 TDZ → 白屏）。种子手上本来就有归属
 * （就是它写进商品行里的那个 `MOCK_OWNER_ID`），索性让它自己传进来 ——
 * 商品的归属与它分类的归属也就**由同一份值派生**，不会各自演化。
 *
 * 请求处理里一律不传，走默认值 —— 那边 `currentMockUserId()` 是准的。
 */
function mockEnsureProductCategory(name: string, ownerId: string = currentMockUserId()): string {
  const clean = (name ?? '').trim()
  if (!clean) return ''
  const hit = productCategoriesDb.find(
    (c) => c.userId === ownerId && c.name.toLowerCase() === clean.toLowerCase(),
  )
  if (hit) return hit.name
  const now = new Date().toISOString()
  const top = productCategoriesDb
    .filter((c) => c.userId === ownerId)
    .reduce((m, c) => Math.max(m, c.sortOrder), 0)
  productCategoriesDb.push({
    id: `pc_mock_${productCategoriesDb.length + 1}`,
    userId: ownerId,
    name: clean,
    sortOrder: top + 10,
    createdAt: now,
    updatedAt: now,
  })
  return clean
}

/** 演示用的图：内联 SVG，不依赖网络，mock 模式下也能看清封面与图组 */
function mockShot(label: string, hue: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="480">` +
    `<rect width="480" height="480" fill="hsl(${hue} 55% 93%)"/>` +
    `<text x="240" y="250" font-size="34" text-anchor="middle" ` +
    `fill="hsl(${hue} 35% 42%)">${label}</text></svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

function seedProductAssets(): void {
  const shots = (label: string, hue: number) => [
    { url: mockShot(`${label} 主图`, hue), name: `${label}-main.svg` },
    { url: mockShot(`${label} 场景`, hue + 18), name: `${label}-scene.svg` },
  ]
  const now = new Date().toISOString()
  //              标题            SKU      卖点              语言      平台       色相  分类
  const seed: Array<[string, string, string, string, string, number, string]> = [
    ['硅藻土沥水垫', 'HYS-001', '0.5s 速吸\n可折叠收纳\n耐高温 200℃', 'zh-CN', 'amazon', 210, '家居日用'],
    ['棉质撞色镶边半身裙', 'CS-002', 'Soft cotton blend\nContrast piping\nElastic waist', 'en-US', 'shopify', 18, '服饰箱包'],
  ]
  seed.forEach(([title, sku, points, language, platform, hue, category], i) => {
    const images = shots(sku, hue)
    productAssetsDb.push({
      id: `pa_mock${i + 1}`,
      userId: MOCK_OWNER_ID,
      title,
      sku,
      sellingPoints: points,
      language,
      platform,
      // 分类名由 `mockEnsureProductCategory` 规范后再落 —— 别直接写种子里那个字面量。
      // ⚠️ 归属**必须显式传** `MOCK_OWNER_ID`：这里在模块初始化期，
      // `currentMockUserId()` 取不到值（TDZ → 整个 app 白屏）。
      category: mockEnsureProductCategory(category, MOCK_OWNER_ID),
      images,
      coverUrl: images[0].url,
      coverLocal: '',
      source: 'manual',
      note: '',
      createdAt: now,
      updatedAt: now,
    })
  })
}
seedProductAssets()
/**
 * 「已清理」的任务 id —— mock 版的软删位。
 *
 * ⚠️ **刻意不删 `jobs` 里的条目**：真实后端也只是把 `hidden` 置 1，
 * 记录留在库里可恢复。mock 若在这里 `jobs.delete()`，「清空后还能找回」
 * 这条链路在 mock 下就永远验不出来 —— 又是一个「切到 mock 看起来没问题」的假绿。
 */
const hiddenJobIds = new Set<string>()

/* --------------------------- 资产库（离线兜底） --------------------------- */

function categoryOfWork(w: WorkItem): string {
  return w.assetCategory ?? classifyLocal(w.appSlug, w.workType, w.sourcePage)
}

/**
 * 把作品与上传素材合并成资产库视图。
 * 规则与后端 `/api/asset-library` 一致：先合并，再筛选，最后排序。
 */
function mergeAssetLibrary(params?: AssetLibraryQuery): AssetLibraryItem[] {
  const category = params?.category ?? 'all'
  const origin = params?.origin ?? 'all'
  const kind = params?.kind ?? 'all'
  const kw = (params?.q ?? '').trim().toLowerCase()

  const items: AssetLibraryItem[] = [
    ...worksDb.map((w) => ({
      id: w.id,
      title: w.title,
      url: w.url,
      thumbUrl: w.thumbUrl || w.url,
      kind: w.workType,
      assetCategory: categoryOfWork(w),
      origin: 'generated' as const,
      sourcePage: w.sourcePage,
      sourceModel: w.sourceModel,
      appSlug: w.appSlug ?? '',
      prompt: w.prompt,
      contentText: w.contentText,
      favorite: w.favorite,
      // 任务 id 透传：缺失（老数据 / mock 种子）时前端按「单张卡」渲染，不硬凑分组
      jobId: w.jobId ?? '',
      createdAt: w.createdAt,
    })),
    ...assetsDb.map((a) => ({
      id: a.id,
      title: a.name || '上传素材',
      url: a.url,
      thumbUrl: a.thumbUrl || a.url,
      kind: a.kind,
      assetCategory: a.assetCategory ?? classifyLocal(a.context, a.kind),
      origin: 'upload' as const,
      sourcePage: '',
      sourceModel: '',
      appSlug: a.context ?? '',
      favorite: false,
      createdAt: a.createdAt,
    })),
  ]

  return items
    .filter((i) => category === 'all' || i.assetCategory === category)
    .filter((i) => origin === 'all' || i.origin === origin)
    .filter((i) => kind === 'all' || i.kind === kind)
    .filter((i) =>
      kw
        ? i.title.toLowerCase().includes(kw) || (i.prompt ?? '').toLowerCase().includes(kw)
        : true,
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
}

let seq = 1000
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(seq++).toString(36)}`

const rand = (seed: string) => {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return () => {
    h ^= h << 13
    h ^= h >>> 17
    h ^= h << 5
    return ((h >>> 0) % 100000) / 100000
  }
}

/** 用确定性占位图，保证离线可用且同一任务每次刷新都是同一张图 */
function placeholder(seed: string, w: number, h: number): string {
  return `https://picsum.photos/seed/${encodeURIComponent(seed)}/${w}/${h}`
}

function dimsFor(aspectRatio: string): [number, number] {
  // 平台详情页给的是**成品像素**（A+ 的 970x600 / 300x400 …）——
  // 直接按它出占位图，预览里的比例才和真实交付一致。
  // 不认这一条的话，A+ 六个模块在 mock 里会全变成同一个 16:9，
  // 而「比例不对」正是这次要修的问题，预览不能自己再错一遍。
  const px = /^(\d{2,5})x(\d{2,5})$/i.exec(aspectRatio.trim())
  if (px) return [Number(px[1]), Number(px[2])]

  switch (aspectRatio) {
    case '1:1':
      return [1024, 1024]
    case '3:4':
      return [900, 1200]
    case '9:16':
      return [810, 1440]
    case '4:3':
      return [1200, 900]
    default:
      return [1280, 720]
  }
}

/**
 * 价目表从模型族派生，不再单独手写。
 *
 * 以前这里是两张手写表，和后端 pricing.py 各自漂移
 * （flare 这里写 0.08、后端是 0.10），导致 mock 模式与真实模式报出两个价。
 * 现在单一来源是 config/modelFamilies.ts。
 */
function priceKeys(f: ModelFamilyInfo): string[] {
  return [f.id, f.textSlug, f.imageSlug].filter((x): x is string => !!x)
}

const IMAGE_PRICE: Record<string, number> = Object.fromEntries(
  FALLBACK_FAMILIES.filter((f) => f.kind === 'image').flatMap((f) =>
    priceKeys(f).map((k) => [k, f.unitPrice]),
  ),
)

const VIDEO_PER_SECOND: Record<string, number> = Object.fromEntries(
  FALLBACK_FAMILIES.filter((f) => f.kind === 'video').flatMap((f) =>
    priceKeys(f).map((k) => [k, f.unitPrice]),
  ),
)

function priceOf(req: PricePreviewRequest): { price: number; basis: string; free: boolean } {
  // 文案类走文本计价，与生图/生视频分开
  if (req.appSlug?.startsWith('copy')) {
    return { price: 0.02, basis: '文案生成 ¥0.02/次（按 token 结算，多退少补）', free: false }
  }
  const slug = req.model ?? DEFAULT_FAMILY_ID.image
  // 表单里传的是模型族 id；也兼容历史数据里的路由 slug
  const family = FALLBACK_FAMILIES.find((f) => priceKeys(f).includes(slug))
  const kind = family?.kind ?? 'image'

  if (kind === 'video') {
    const perSec = family?.unitPrice ?? VIDEO_PER_SECOND[slug] ?? 0.3
    const dur = Number((req.inputs?.duration as number) ?? 5)
    const n = Number(req.outputs ?? 1)
    return {
      price: Number((perSec * dur * n).toFixed(3)),
      basis: `按秒计价 ¥${perSec}/秒 × ${dur} 秒 × ${n} 条`,
      free: false,
    }
  }
  const unit = family?.unitPrice ?? IMAGE_PRICE[slug] ?? 0.1
  const n = Number(req.outputs ?? 1)
  const hi = req.resolution === '4k' ? 1.8 : req.resolution === '2k' ? 1.2 : 1
  return {
    price: Number((unit * n * hi).toFixed(3)),
    basis: `按张计价 ¥${unit}/张 × ${n} 张${hi !== 1 ? ` × ${hi} 分辨率系数` : ''}`,
    free: false,
  }
}

/* --------------------- 会员体系（mock 的内存态） --------------------- */

/**
 * mock 会员表。**与后端 `accounts.SEED_USERS` 同名同额度同口令**。
 *
 * 刻意对齐种子而不是随手编几个：切到 http 模式时账号不用重记一套；
 * 也只有这样，「同一个账号在 mock 与真后端上看到的额度不一样」这种
 * 只会在演示时被发现的困惑才不会出现。
 *
 * `plan` 一律带「原型演示」字样 —— 让它在界面上**看得出是假的**。
 *
 * ⚠️ 2026-10-07 拆表后这里**没有 `admin` 那一行**了。它挪去了 `mockAdmins`
 * （见下）—— 两张表、两套登录。留着它会让 mock 模式与真后端分叉：
 * 真后端的会员登录入口查不到 `admin`，mock 里却能登进去，
 * 于是「拆表到底拆没拆」在演示环境里根本看不出来。
 */
const MOCK_NOW = new Date().toISOString()

function makeMockUser(id: string, account: string, displayName: string, coins: number): AuthUser {
  return {
    id,
    account,
    displayName,
    coins,
    plan: '原型演示（mock 账号）',
    status: 'active',
    createdAt: MOCK_NOW,
    updatedAt: MOCK_NOW,
  }
}

const mockUsers: AuthUser[] = [
  makeMockUser(MOCK_OWNER_ID, 'songshuai@hsy.com', '松鼠AIGC', 20000),
  makeMockUser('u-mock-amazon', 'amazon@hsy.com', 'Amazon 线', 10000),
  makeMockUser('u-mock-ozon', 'ozon@hsy.com', 'Ozon 线', 10000),
  makeMockUser('u-mock-tiktok', 'tiktok@hsy.com', 'TikTok 线', 8000),
  makeMockUser('u-mock-demo', 'demo@hsy.com', '演示账号', 3000),
]

/** 口令独立一张表：`AuthUser` 是**下发给界面**的形状，里面不会有口令字段。 */
const MOCK_PASSWORDS: Record<string, string> = {
  'songshuai@hsy.com': '123456',
  'amazon@hsy.com': '123456',
  'ozon@hsy.com': '123456',
  'tiktok@hsy.com': '123456',
  'demo@hsy.com': '123456',
}

/**
 * mock **后台**账号表 —— 与 `mockUsers` 刻意是**两份独立的东西**。
 *
 * 字段集就不同（没有 coins / plan，多一个 `lastLoginAt`），
 * 这正是两张表分开的意义。别为了省几行让它复用 `AuthUser`：
 * 那样 `role` / `coins` 会重新长回来，而 mock 模式**是默认模式**
 * （`VITE_API_MODE` 不设就是 mock），它一分叉，界面上看到的就是错的。
 */
const mockAdmins: AdminUser[] = [
  {
    id: 'adm_mock_root',
    account: 'admin',
    displayName: '系统管理员',
    status: 'active',
    lastLoginAt: MOCK_NOW,
    createdAt: MOCK_NOW,
    updatedAt: MOCK_NOW,
  },
]

const MOCK_ADMIN_PASSWORDS: Record<string, string> = { admin: 'admin12345' }

/** 当前登录的账号 id。空串 = 未登录（刷新页面就没了，mock 不落盘）。 */
let mockSessionUserId = ''

/**
 * 当前登录的**后台**账号 id。**与上面那个是两个变量** ——
 * 这正是「两套登录态」在 mock 里的体现：可以同时登着工作台与后台，
 * 退出其中一个不影响另一个。合并成一个变量的话，mock 模式就无法演示
 * 用户明确要求的那个行为（「退出后台不会把工作台踢下线」）。
 */
let mockSessionAdminId = ''

/**
 * 后台门卫（mock）。
 *
 * ⚠️ 判据是**后台会话**，不是「当前会员是不是管理员」——
 * 会员表里已经没有这个概念了。上一版正是 `me.role !== 'admin'`，
 * 拆表后它会变成一个恒真的判断（会员行上没有 role ⇒ undefined ≠ 'admin' ⇒
 * 全部 403），于是 mock 模式下后台**永远打不开**，而页面只会显示
 * 「该操作仅管理员可用」，让人以为是权限配错了。
 */
function requireMockAdmin(): AdminUser {
  const me = mockAdmins.find((a) => a.id === mockSessionAdminId)
  if (!me) throw new ApiError('登录已过期，请重新登录', 'UNAUTHORIZED', 401)
  if (me.status !== 'active') throw new ApiError('该后台账号已被停用', 'ACCOUNT_DISABLED', 403)
  return me
}

/** 与后端 `runtime_config.mask` 同口径：只留头 4 尾 4，中间一律圆点。 */
function maskKey(secret: string): string {
  if (!secret) return ''
  if (secret.length <= 8) return '••••••'
  return `${secret.slice(0, 4)}••••••${secret.slice(-4)}`
}

/** mock 的额度账本：从初始额度往回推，生成一串自洽的进出账。 */
function mockLedger(userId: string): CoinLedgerItem[] {
  const row = mockUsers.find((u) => u.id === userId)
  if (!row) return []
  const r = rand(`ledger-${userId}`)
  const kinds: CoinLedgerItem['kind'][] = ['spend', 'spend', 'spend', 'grant', 'spend', 'adjust', 'refund']
  const notes: Record<CoinLedgerItem['kind'], string> = {
    spend: '生成任务预扣',
    grant: '开户赠送额度',
    refund: '任务失败退还',
    adjust: '按实际用量结算',
  }
  // 从当前余额往回推：这样最后一条的 balanceAfter == 界面上的余额，
  // 两边对不上会让人以为账本漏了记录。
  let balance = row.coins
  const out: CoinLedgerItem[] = []
  for (let i = 0; i < 24; i++) {
    const kind = kinds[Math.floor(r() * kinds.length)]
    const coins = Number((kind === 'grant' ? 500 + r() * 1500 : -(2 + r() * 60)).toFixed(2))
    out.push({
      id: `lg-${userId}-${i}`,
      createdAt: new Date(Date.now() - i * 1000 * 60 * (23 + r() * 260)).toISOString(),
      kind,
      coins,
      balanceAfter: Number(balance.toFixed(2)),
      ref: kind === 'grant' ? 'admin' : `job_mock_${1000 + i}`,
      note: notes[kind],
    })
    // 往前一条：把这一笔「撤销」回去得到上一笔之后的余额
    balance = kind === 'grant' ? balance - coins : balance + Math.abs(coins)
    if (balance < 0) balance = 0
  }
  return out
}

/**
 * 后台管理里的上游清单（mock）。
 *
 * 结构与分组**照抄后端 `runtime_config.describe()`**：分组口径（text / image / video）、
 * 每行有哪些输入框（`fields`）、哪些上游脚本里自带 Key（`builtinKey`）全都一致。
 * 编一套不一样的分组，只会让人切到真后端时发现「后台界面长得不一样」。
 */
/**
 * 适配器家族（与后端 `runtime_config.FAMILIES` 同形）。
 *
 * 家族是**代码常量**：一条通道能跑起来靠的是家族背后那段真实代码
 * （skill 脚本 + 适配器分支），所以后台只能在这些家族下**新增实例**，
 * 不能凭空造一个家族出来。
 */
const MOCK_FAMILIES: UpstreamFamily[] = [
  {
    family: 'llm',
    label: '文字模型',
    kinds: ['text'],
    note: '文案 / 提示词改写 / 表单填充走的 LLM（OpenAI 兼容协议）',
    fields: ['baseUrl', 'apiKey', 'model'],
    modelHint: '如 deepseek-flash / deepseek-v4-pro',
    unit: '条',
    builtinKey: true,
  },
  {
    family: 'duomi',
    label: 'duomi 直连',
    kinds: ['image'],
    note: 'duomiapi.com（gpt-image-2）；与网关那条是同一个上游模型',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '张',
    builtinKey: true,
  },
  {
    family: 'gateway',
    label: 'songshuai 网关',
    kinds: ['image', 'video'],
    note: 'www.songshuai.art 网关（image2 / nano-banana / minimax-h3 等）',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '',
    builtinKey: false,
  },
  {
    family: 'youkou',
    label: '贰春 API',
    kinds: ['video'],
    note: 'api.erchun.youkou.cc（Seedance / Wan / MiniMax 等 14 个模型）',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '',
    builtinKey: true,
  },
  {
    family: 'wuyin',
    label: '速创 API',
    kinds: ['video'],
    note: 'api.wuyinkeji.com（Google Omni 全模态视频）',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '',
    builtinKey: true,
  },
  {
    family: 'runninghub',
    label: 'RunningHub',
    kinds: ['video'],
    note: 'www.runninghub.cn（数字人口型同步，按 RH 币计费）',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '',
    builtinKey: true,
  },
  {
    family: 'apilio',
    label: 'apilio 配音',
    kinds: ['video'],
    note: 'api.apilio.ai（数字人口播的语音合成，音色试听也走它）',
    fields: ['baseUrl', 'apiKey'],
    modelHint: '',
    unit: '',
    builtinKey: true,
  },
]

/**
 * 造一条**默认（seed）通道**的 mock 行。
 *
 * `builtin: true` / `active: true` 是默认态：种子通道就是代码里写的那条，
 * 也是该家族默认生效的那条。用户新增的通道这两项都是 false
 * （要手动点「启用」才生效）—— 这个差别在界面上必须看得出来。
 */
function mockChannel(
  family: string,
  over: Partial<UpstreamItem> & Pick<UpstreamItem, 'label' | 'note' | 'fields' | 'baseUrl'>,
): UpstreamItem {
  const spec = MOCK_FAMILIES.find((f) => f.family === family)
  return {
    id: family,
    family,
    channel: family,
    kinds: [...(spec?.kinds ?? [])],
    builtin: true,
    active: true,
    available: true,
    unitPrice: null,
    unit: spec?.unit ?? '',
    modelHint: spec?.modelHint ?? '',
    model: '',
    apiKeyMasked: '',
    hasApiKey: false,
    builtinKey: Boolean(spec?.builtinKey),
    overridden: [],
    ...over,
  }
}

/**
 * 网关那条通道。
 *
 * 🔴 **图与视频两组里看到的是同一条记录** —— 网关同时供图与视频，它在库里
 * 只有一行。下面两个分组都引用它，而 `mockChannels()` 会按 id 去重，
 * 于是「这个家族还剩几条」不会被数成两倍（那个数字决定删除按钮能不能点）。
 */
const MOCK_UPSTREAMS_GATEWAY = mockChannel('gateway', {
  label: 'songshuai 网关',
  note: 'www.songshuai.art 网关（image2 / nano-banana / minimax-h3 等）',
  fields: ['baseUrl', 'apiKey'],
  baseUrl: 'https://www.songshuai.art/v1',
})

const MOCK_UPSTREAMS: UpstreamCatalog = {
  families: MOCK_FAMILIES,
  groups: [
    {
      kind: 'text',
      label: '文字模型',
      sub: '文案 / 改写 / 表单填充',
      items: [
        mockChannel('llm', {
          label: '文字模型',
          note: '文案 / 提示词改写 / 表单填充走的 LLM（OpenAI 兼容协议）',
          fields: ['baseUrl', 'apiKey', 'model'],
          modelHint: '如 deepseek-flash / deepseek-v4-pro',
          baseUrl: 'https://api.deepseek.com/v1',
          model: 'deepseek-flash',
          apiKeyMasked: 'sk-d••••••9f2c',
          hasApiKey: true,
        }),
      ],
    },
    {
      kind: 'image',
      label: '图片模型',
      sub: '出图链路',
      items: [
        mockChannel('duomi', {
          label: 'duomi 直连',
          note: 'duomiapi.com（gpt-image-2）；与网关那条是同一个上游模型',
          fields: ['baseUrl', 'apiKey'],
          baseUrl: 'https://duomiapi.com',
          apiKeyMasked: 'sk-a••••••7b31',
          hasApiKey: true,
        }),
        // ⚠️ 与下面视频组里那条是**同一条记录**（网关同时供图与视频）。
        // 刻意复用同一个常量而不是抄一遍内容：抄一遍就成了两处真源，
        // 改一处漏一处，而「按 id 去重」那条逻辑就再也测不到了。
        MOCK_UPSTREAMS_GATEWAY,
      ],
    },
    {
      kind: 'video',
      label: '视频模型',
      sub: '出片链路（含数字人）',
      items: [
        MOCK_UPSTREAMS_GATEWAY,
        mockChannel('youkou', {
          label: '贰春 API',
          note: 'api.erchun.youkou.cc（Seedance / Wan / MiniMax 等 14 个模型）',
          fields: ['baseUrl', 'apiKey'],
          baseUrl: 'https://api.erchun.youkou.cc',
          apiKeyMasked: 'sk-y••••••3d18',
          hasApiKey: true,
        }),
        mockChannel('wuyin', {
          label: '速创 API',
          note: 'api.wuyinkeji.com（Google Omni 全模态视频）',
          fields: ['baseUrl', 'apiKey'],
          baseUrl: 'https://api.wuyinkeji.com',
          apiKeyMasked: 'sk-w••••••5e77',
          hasApiKey: true,
        }),
        mockChannel('runninghub', {
          label: 'RunningHub',
          note: 'www.runninghub.cn（数字人口型同步，按 RH 币计费）',
          fields: ['baseUrl', 'apiKey'],
          baseUrl: 'https://www.runninghub.cn',
        }),
        mockChannel('apilio', {
          label: 'apilio 配音',
          note: 'api.apilio.ai（数字人口播的语音合成，音色试听也走它）',
          fields: ['baseUrl', 'apiKey'],
          baseUrl: 'https://api.apilio.ai',
          apiKeyMasked: 'sk-p••••••1a04',
          hasApiKey: true,
        }),
      ],
    },
  ],
}

/* ----------------------------- 通道增删改（mock） ----------------------------- */
/*
 * 下面这几个函数存在的理由：`VITE_API_MODE` 的**默认值是 mock**，
 * 后台页一打开就要能跑。不补的话切到 mock 模式打开 `/admin`，点「添加通道」直接
 * 报「该方法未实现」—— 而「mock 只在真后端那边能用」这种不对称每次都有人踩。
 *
 * ⚠️ 这里是**演示级**近似，不是后端的完整复刻：它不碰环境变量、不构造适配器、
 * 也没有「凭据残留」那一层（那些只有真进程里才有意义）。但**清单语义与后端一致**：
 * 新增不自动启用、同族只有一条 active、最后一条不许删、删启用那条要把启用态
 * 落到同族剩下的第一条、id 用过（含删过的）不许复用。
 *
 * 实现走的是后端那个**增量口径**：`MOCK_UPSTREAMS` 是 seed 形状，当前清单由
 * seed 快照 ± 增量现算。直接改清单的话「恢复默认」就没有基准了。
 */

/** seed 的扁平清单（按 id 去重：`gateway` 同时在图与视频两组，是同一条记录）。 */
function mockSeedChannels(): UpstreamItem[] {
  const seen = new Set<string>()
  const out: UpstreamItem[] = []
  for (const g of MOCK_UPSTREAMS.groups) {
    for (const i of g.items) {
      if (seen.has(i.id)) continue
      seen.add(i.id)
      out.push(i)
    }
  }
  return out
}

/** seed 快照（模块加载时抓一次），给「恢复默认」当基准。 */
const MOCK_CHANNELS_SEED: UpstreamItem[] = mockSeedChannels()

/** 当前通道清单。初始 = seed；增删都作用在它上面。 */
let mockChannelState: UpstreamItem[] = structuredClone(MOCK_CHANNELS_SEED)

/**
 * 已被删除的通道 id（墓碑）。
 *
 * 只从清单里挪走是不够的：那样「删掉 `duomi-2` 再新增一条」会**复用**这个 id，
 * 而这条 id 的历史字段覆盖（旧 Key）还在 —— 后端的做法是把它记进 `removed`
 * 从而让 id 永不复用，这里对齐。
 */
const mockRemovedIds = new Set<string>()

/**
 * family → 当前启用的通道 id。
 *
 * 🔴 `active` 是**算出来的，不存第二份**。存在通道对象上的话，「把 A 设成启用」
 * 必须同时把兄弟节点设成未启用 —— 两处状态要同步，漏一处就是界面上两条都亮着。
 * 同后端 `_resolve()`：没被显式切过时，落点是该家族的 seed 那条。
 */
const mockActiveOf = new Map<string, string>()

/** 某家族**当前启用**的通道 id。切过的那条没了 ⇒ 退到 seed 那条，再退到第一条。 */
function mockActiveId(family: string): string {
  const pool = mockChannelState.filter((c) => c.family === family)
  const picked = mockActiveOf.get(family)
  if (picked && pool.some((c) => c.id === picked)) return picked
  return (pool.find((c) => c.id === family) ?? pool[0])?.id ?? ''
}

/** 带 `active` 的清单 —— **唯一一处**把 `active` 算出来的地方。 */
function mockChannels(): UpstreamItem[] {
  return mockChannelState.map((c) => ({ ...c, active: mockActiveId(c.family) === c.id }))
}

/**
 * 按产出类型分组。
 *
 * 分组口径与后端 `describe()` 一致：`kind ∈ 该通道的 kinds`。**不按 channel 名
 * 猜** —— `gateway` 的 kinds 同时含图与视频，所以它必然出现在两个分组里。
 */
function mockGroups(): UpstreamGroup[] {
  const all = mockChannels()
  return MOCK_UPSTREAMS.groups.map((g) => ({
    kind: g.kind,
    label: g.label,
    sub: g.sub,
    items: all.filter((c) => c.kinds.includes(g.kind)),
  }))
}

/** 改动接口统一回整份新清单（与后端同一口径）。 */
function mockCatalog(extra: Partial<UpstreamCatalogResult> = {}): UpstreamCatalogResult {
  return {
    groups: mockGroups(),
    families: structuredClone(MOCK_UPSTREAMS.families),
    ...extra,
  }
}

/* ------------------------------- 实现 ------------------------------- */

/**
 * 当前登录的会员 id（mock）。
 *
 * ⚠️ 商品资产**按它收口**，与真实后端一致 —— 切账号后列表真的会变。
 * 拿一个写死的字符串当归属，会让「mock 下切账号仍看到同一批货」
 * 成为一条永远验不出来的链路（真后端那边是按 `user_id` 过滤的）。
 *
 * 定义在这里而不是紧挨着 `productAssetsDb`：它引用的 `mockSessionUserId`
 * 声明在文件中部，写在使用处之前会触发 `no-use-before-define`。
 */
function currentMockUserId(): string {
  return mockSessionUserId || mockUsers[0]?.id || ''
}

/** 商品搜索（mock）：标题 / SKU / 卖点三列，与后端 `_PRODUCT_ASSET_SEARCH_COLS` 对齐 */
function mockFilterProductAssets(
  params: ProductAssetQuery = {},
): { items: ProductAsset[]; total: number } {
  let rows = productAssetsDb.filter((a) => a.userId === currentMockUserId())
  const q = (params.q ?? '').trim().toLowerCase()
  if (q) {
    rows = rows.filter((a) =>
      [a.title, a.sku, a.sellingPoints].join('\n').toLowerCase().includes(q),
    )
  }
  if (params.language) rows = rows.filter((a) => a.language === params.language)
  if (params.platform) rows = rows.filter((a) => a.platform === params.platform)
  // 分类三态：不传 = 全部；哨兵 = 未分类；其它 = 精确匹配（**不是 includes** ——
  // 后端是 `category = ?`，mock 用模糊匹配会让「A 类」的筛选把「AB 类」也捞进来，
  // 而这条差异只在 mock 下看得到，真后端上永远不复现）
  if (params.category === MOCK_UNCATEGORIZED) rows = rows.filter((a) => !a.category.trim())
  else if (params.category) rows = rows.filter((a) => a.category === params.category)
  const total = rows.length
  const page = Math.max(1, params.page ?? 1)
  const perPage = Math.min(500, Math.max(1, params.perPage ?? 100))
  const start = (page - 1) * perPage
  return { items: rows.slice(start, start + perPage).map((a) => ({ ...a })), total }
}

/**
 * 造一条商品（mock）。**封面 = 第一张图，在这里算一次** ——
 * 与后端 `db._product_asset_fields` 同一口径。mock 自己另算一份的话，
 * 「删掉第一张图、封面却没换」这类问题在 mock 下永远看不出来。
 */
function mockBuildProductAsset(
  req: CreateProductAssetRequest,
  source: 'manual' | 'import',
): ProductAsset {
  const images: ProductAssetImage[] = (req.images ?? [])
    .filter((i) => (i.url ?? '').trim())
    .slice(0, 30)
    .map((i) => ({
      url: i.url,
      thumbUrl: i.thumbUrl ?? '',
      localUrl: i.localUrl ?? '',
      name: i.name ?? '',
    }))
  const now = new Date().toISOString()
  return {
    id: `pa_mock_${Math.random().toString(36).slice(2, 10)}`,
    userId: currentMockUserId(),
    title: req.title.trim(),
    sku: req.sku ?? '',
    sellingPoints: req.sellingPoints ?? '',
    language: (req.language ?? '').trim() || 'zh-CN',
    platform: req.platform ?? '',
    // 分类名先登记进分类表（与后端 `db.create_product_asset` 同一步），
    // 落进商品的是**表里的写法** —— 两边差一个大小写，按分类筛就漏货
    category: mockEnsureProductCategory(req.category ?? ''),
    images,
    coverUrl: images[0]?.url ?? '',
    coverLocal: images[0]?.localUrl || images[0]?.thumbUrl || '',
    source,
    note: req.note ?? '',
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * 筛选条的真实分布（mock）：与后端 `db.product_asset_facets` 同一口径。
 *
 * ⚠️ `categories` 与前两者**不是同一类东西**：语言/平台是「库里有什么值」的统计，
 * 分类是**清单**（用户建的空分类也要出现）。所以它来自 `productCategoriesDb`，
 * 商品数只是挂在每行上的那一列 —— 抄后端那段注释里的理由。
 */
function mockProductAssetFacets(): ProductAssetFacets {
  const rows = productAssetsDb.filter((a) => a.userId === currentMockUserId())
  const tally = (pick: (a: ProductAsset) => string) => {
    const counter = new Map<string, number>()
    rows.forEach((a) => {
      const key = (pick(a) ?? '').trim()
      if (key) counter.set(key, (counter.get(key) ?? 0) + 1)
    })
    return [...counter.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((x, y) => y.count - x.count)
  }
  // 分类计数按小写归一，和商品上的写法大小写不同也能算进同一个（抄后端 `_norm_category`）
  const byCategory = new Map<string, number>()
  let uncategorized = 0
  rows.forEach((a) => {
    const name = a.category.trim()
    if (!name) {
      uncategorized += 1
      return
    }
    const key = name.toLowerCase()
    byCategory.set(key, (byCategory.get(key) ?? 0) + 1)
  })
  return {
    total: rows.length,
    images: rows.reduce((n, a) => n + a.images.length, 0),
    uncategorized,
    languages: tally((a) => a.language),
    platforms: tally((a) => a.platform),
    categories: productCategoriesDb
      .filter((c) => c.userId === currentMockUserId())
      .sort((x, y) => x.sortOrder - y.sortOrder || x.name.localeCompare(y.name))
      .map((c) => ({
        value: c.name,
        count: byCategory.get(c.name.toLowerCase()) ?? 0,
        id: c.id,
        sortOrder: c.sortOrder,
      })),
  }
}

export const mockClient: ApiClient = {
  mode: 'mock',

  async listModels(kind?: 'image' | 'video'): Promise<ModelFamilyInfo[]> {
    await delay(120)
    return kind ? FALLBACK_FAMILIES.filter((f) => f.kind === kind) : FALLBACK_FAMILIES
  },

  /**
   * mock 模式没有后端技能目录，所以**恒为 enabled:false + 空清单**。
   *
   * 不伪造一份假清单：这个开关的整个价值在于「模型读到的名字必须真实存在」，
   * mock 里编几个看起来对的技能名，只会让人在真后端上照着假名字去排查。
   */
  async listSkills(): Promise<SkillIndex> {
    await delay(80)
    return { enabled: false, count: 0, indexChars: 0, skillsDir: '', skills: [] }
  },

  /**
   * 音色清单（mock）。
   *
   * 给的是**真清单的一个子集**（与 `config/voices.ts` 的兜底同一批），
   * 不编造音色 ID：真正的清单由后端从上游读（默认引擎下有 303 条），
   * mock 里放一批上游不认的假 ID，只会在切回 http 模式时
   * 把「音色怎么少了一大半」变成一次排查。
   */
  async listVoices(): Promise<VoiceCatalog> {
    await delay(80)
    return {
      /*
       * mock 固定按**默认引擎**返回（与 `config/voices.ts` 的兜底同一套口径）：
       * 编一个别的引擎名只会让 mock 与真后端长得不一样。
       *
       * ⚠️ 这四行（engine / engineLabel / model / skill）必须在**换默认引擎时同步改**。
       * 2026-10-08 从网关的 `openai` 档切到官方直连 `minimax-direct`，
       * 而这里的名字当时漏改了 —— mock 并不报错，它只是**说出另一个引擎的名字**，
       * 于是「界面上写着 OpenAI，实际是 MiniMax 在合成」这种最难查的错就有了第二处来源。
       * 后端那侧由 `test_tts_voices.py` 跨仓库钉住（它会读 `config/voices.ts`），
       * 这四行没有测试覆盖，只能靠这条注释 + 改的人自己记得。
       */
      engine: 'minimax-direct',
      engineLabel: 'MiniMax 官方直连',
      default: DEFAULT_VOICE_ID,
      model: 'speech-2.8-hd',
      skill: 'minimax-tts',
      count: FALLBACK_VOICE_GROUPS.reduce((n, g) => n + g.voices.length, 0),
      labelledCount: FALLBACK_VOICE_GROUPS.reduce((n, g) => n + g.voices.length, 0),
      groups: FALLBACK_VOICE_GROUPS,
      /*
       * 「我的音色」恒为空：那一栏是**上游账号里的克隆音色**，由后端的
       * `voice_clones` 表（镜像）+ `sync_account_clones()` 维护，mock 既没有那张表
       * 也没有上游的 key，无从伪造 —— 与其编几个假 ID 让人以为克隆成功了，
       * 不如空着，让「我的音色」在 mock 下诚实地空。
       */
      mine: [],
    }
  },

  /**
   * 音色试听（mock）。
   *
   * **mock 里做不出这件事，所以如实报错** —— 这是本文件里唯一一个直接抛错的接口，
   * 理由值得写下来：试听的**全部价值**就是「让你真的听到这个音色」。
   * 返回一段静音文件、或一段通用提示音，用户听到的都不是这个音色，
   * 而界面会显示「试听中 → 播放完了」——**看起来成功了，实际上一无所获**，
   * 比「这条路在 mock 里走不通」难排查得多。
   * （与 `fillForm` 那套「返回带 mock 标记的内容」的区别：文案能标记，
   * 声音没法标记 —— 它就是要被你听见的那个东西。）
   *
   * 要试听请把 `VITE_API_MODE` 切成 `http` 连上后端（真调用一次 TTS，按次计费）。
   */
  async previewVoice(req: VoicePreviewRequest): Promise<VoicePreviewResponse> {
    await delay(120)
    throw new ApiError(
      `mock 模式无法试听「${req.voice}」：试听要真的调一次语音合成，mock 里没有上游。` +
        `把 VITE_API_MODE 切成 http 连上后端即可。`,
      'VOICE_PREVIEW_UNAVAILABLE',
      0,
    )
  },

  /**
   * 当前身份（mock）。
   *
   * **跟着登录的那个会员走**，不再返回一个写死的「演示用户」：会员体系上线后，
   * 界面上「我是谁 / 我还有多少额度」全部要跟账号一致 —— 写死的话，
   * 换账号登进去，顶栏还显示着上一个人的名字与余额，看起来像登录没生效。
   * 未登录时回落第一个种子账号（与后端放行模式回落主账号同一口径）。
   *
   * ⚠️ 拆表后这里**没有 `role`** —— 会员没有「是不是管理员」这个属性。
   * `mockAdmins` 里那个人在这个接口里查不到，这是刻意的。
   */
  async me(): Promise<Identity> {
    await delay(120)
    const row = mockUsers.find((u) => u.id === mockSessionUserId) ?? mockUsers[0]
    return {
      userId: row.id,
      displayName: row.displayName,
      coinBalance: row.coins,
      plan: row.plan,
      account: row.account,
      coinUsed: 0,
    }
  },

  async ping(): Promise<void> {
    // mock 层永远「活着」：预检是给真实后端僵死准备的闸门，这里不拦。
    return
  },

  async upload(file: File): Promise<UploadResult> {
    // mock 用 objectURL，刷新后失效——真实后端会返回 CDN 地址
    await delay(420 + Math.random() * 380)
    const url = URL.createObjectURL(file)
    const dims = await imageSize(url).catch(() => [0, 0])
    return {
      downloadUrl: url,
      fileName: file.name,
      size: file.size,
      mime: file.type,
      width: dims[0] || undefined,
      height: dims[1] || undefined,
    }
  },

  async pricePreview(req: PricePreviewRequest): Promise<PricePreviewResponse> {
    await delay(90)
    const { price, basis, free } = priceOf(req)
    return {
      ok: true,
      estimatedPrice: price,
      currency: 'CNY',
      isFreeThisCall: free,
      basis,
    }
  },

  /**
   * 提示词改写（mock）。
   *
   * **不伪造一份「像真模型写的」提示词**：这里只把原文做一处可辨识的加工，
   * 并在开头写明这是本地 mock。理由与上面 `listSkills` 那条一样 ——
   * mock 里编出以假乱真的产出，用户到真后端上会拿它当基准去排查
   * 「为什么真模型写的跟 mock 不一样」，而那个基准根本不存在。
   *
   * `cost` 恒定 0：这一次确实没花钱。mock 里报 ¥0.10 是假账。
   */
  async refinePrompt(req: RefinePromptRequest): Promise<RefinePromptResponse> {
    await delay(600)
    const base = req.prompt.trim() || req.brief.trim()
    return {
      prompt: [
        '（mock 产物 · 未调用真实模型）',
        base,
        '',
        /* 尾注跟着场景走：`video-prompt` 汇总的是成片提示词，回一句「镜头细化」
           会让人以为这个按钮改的是分镜图（那是 `storyboard` 那一档）。 */
        req.kind === 'video-prompt'
          ? '【成片提示词（mock 示例，真实汇总会写全镜头、规格与「零文字画面」硬约束）】'
          : '【镜头细化（mock 示例，真实改写会逐镜写到景别 / 机位 / 光线）】',
        '· 镜头 1：中景，固定机位，自然侧光 —— 主体进入画面并停稳',
      ].join('\n'),
      model: 'mock-llm',
      cost: 0,
      /* 没花钱就没扣额度 —— 与 `cost: 0` 同一件事的积分口径 */
      coins: 0,
      tokens: 0,
      elapsedMs: 600,
    }
  },

  /**
   * 表单填充（mock）。
   *
   * 与上面 `refinePrompt` 同一条原则：**不伪造一份像真模型写的文案**。
   * 每个字段都带着可辨识的 mock 标记，用户不会拿它当「真模型大概会这么写」的
   * 基准去排查差异 —— 那个基准根本不存在。`cost` 恒 0：这一次确实没花钱。
   *
   * ⚠️ **键必须按 `kind` 给**（取自 `FILL_FORM_FIELDS`，与真实后端同一份清单）。
   * 早先这里写死 banner 那四个键，于是别的页面接上按钮后，
   * 白名单一筛剩 0 个字段、直接抛「模型没有返回可用的字段」——
   * mock 模式下看着像功能坏了，其实只是 mock 没跟着场景走。
   */
  async fillForm(req: FillFormRequest): Promise<FillFormResponse> {
    await delay(500)
    const kind = req.kind ?? 'banner'
    const fields: Record<string, string> = {}
    for (const key of FILL_FORM_FIELDS[kind] ?? []) {
      fields[key] = `（mock · 未调用真实模型）`
    }
    return { fields, model: 'mock-llm', cost: 0, coins: 0, tokens: 0, elapsedMs: 500 }
  },

  /**
   * 参考视频分析（mock）。
   *
   * 与上面两条同一条原则：**不伪造一份像真模型写出来的清单**。每一行都带可辨识的
   * mock 标记 —— 否则用户会拿它当「真模型大概会这么分析」的基准去排查差异，
   * 而那个基准根本不存在。`cost` 恒 0：这一次确实没花钱。
   *
   * ⚠️ 这里**故意不返回 `parsed: false`**。返回 false 界面会退化成「展示原文」，
   * 镜头清单的排版就完全看不到 —— 而 mock 模式的价值恰恰是「不花钱也能看布局」。
   * 代价是必须把「这是假的」写进每一行（见上面那条原则）。
   */
  async analyzeVideo(req: AnalyzeVideoRequest): Promise<AnalyzeVideoResponse> {
    await delay(700)
    return {
      parsed: true,
      shotCount: 3,
      rhythm: '（mock）中速，平均每个镜头 2 秒',
      shots: [
        '（mock · 未调用真实模型）开场：产品全景，缓慢推镜',
        '（mock · 未调用真实模型）中段：细节特写，固定镜头',
        '（mock · 未调用真实模型）收尾：使用场景，拉镜',
      ],
      style: '（mock）柔光、浅色背景、室内场景',
      raw: '（mock · 未调用真实模型）',
      model: 'mock-vl',
      cost: 0,
      coins: 0,
      tokens: 0,
      elapsedMs: 700,
    }
  },

  async run(req: GenerateRequest): Promise<RunResponse> {
    await delay(260 + Math.random() * 240)
    const jobId = nextId('job')
    const job: MockJob = {
      jobId,
      appSlug: req.appSlug,
      title: String(req.inputs.__title ?? req.appSlug),
      sourcePage: String(req.inputs.__sourcePage ?? req.appSlug),
      model: req.model ?? 'gpt-image-2',
      prompt: req.prompt,
      createdAt: Date.now(),
      estimatedMs: req.estimatedMs ?? 45000,
      outputs: req.outputs ?? 1,
      aspectRatio: req.aspectRatio ?? '1:1',
      ratios: req.ratios?.length ? req.ratios : undefined,
      batchId: req.batchId,
      forceFail: Boolean(req.inputs.__forceFail),
    }
    // 文案类走同步返回，不排队
    if (req.appSlug === 'copywriting') {
      job.textResult = buildCopy(String(req.inputs.channel ?? 'product'), req.inputs)
      jobs.set(jobId, job)
      return { job: { jobId }, state: 'succeeded', outputs: textOutputs(job) }
    }
    jobs.set(jobId, job)
    return { job: { jobId }, state: 'queued' }
  },

  async poll(appSlug: string, jobId: string): Promise<PollResponse> {
    await delay(180)
    const job = jobs.get(jobId)
    if (!job) {
      return { state: 'failed', outputs: [], error: { code: 'JOB_NOT_FOUND', message: '任务不存在或已过期' } }
    }
    const elapsed = Date.now() - job.createdAt
    const pct = progressFrom(elapsed, job.estimatedMs)

    if (job.forceFail && elapsed > 2500) {
      return { state: 'failed', outputs: [], error: { code: 'SUBMIT_FAILED', message: '上游模型返回失败（mock 强制失败）' } }
    }
    if (elapsed < job.estimatedMs) {
      return { state: pct < 8 ? 'queued' : 'running', outputs: [] }
    }
    const outputs = job.textResult
      ? textOutputs(job)
      : imageOutputs(job)
    return {
      state: 'succeeded',
      outputs,
      usage: { consumeCoins: Number((priceOf({ appSlug, model: job.model, outputs: job.outputs }).price * 10).toFixed(1)) },
    }
  },

  async cancel(_appSlug: string, jobId: string): Promise<void> {
    // mock 层任务本来就只在内存里，取消即删除，避免后续 poll 又把它查出来
    jobs.delete(jobId)
  },

  /**
   * mock 版任务历史。
   *
   * 真实后端从 SQLite 读；这里从内存 Map 还原成同一形状 ——
   * 目的是让「任务中心读历史列表」这条链路在 mock 模式下也能跑通，
   * 否则切回 mock 时任务中心会静默变成空列表。
   */
  async listJobs(params) {
    await delay(160)
    const out: ServerJobItem[] = []
    for (const job of jobs.values()) {
      const elapsed = Date.now() - job.createdAt
      const pct = progressFrom(elapsed, job.estimatedMs)
      let state: ServerJobItem['state'] = elapsed < job.estimatedMs ? (pct < 8 ? 'queued' : 'running') : 'succeeded'
      let error: ServerJobItem['error'] = null
      if (job.forceFail && elapsed > 2500) {
        state = 'failed'
        error = { code: 'SUBMIT_FAILED', message: '上游模型返回失败（mock 强制失败）' }
      }
      const outputs =
        state === 'succeeded' ? (job.textResult ? textOutputs(job) : imageOutputs(job)) : []
      const price = priceOf({ appSlug: job.appSlug, model: job.model, outputs: job.outputs }).price
      out.push({
        jobId: job.jobId,
        appSlug: job.appSlug,
        model: job.model,
        provider: 'mock',
        platform: '',
        batchId: job.batchId ?? '',
        assetCategory: 'other',
        sourcePage: job.sourcePage,
        title: job.title,
        prompt: job.prompt,
        state,
        outputs,
        error,
        inputs: {},
        imageUrls: [],
        aspectRatio: job.aspectRatio,
        resolution: '',
        expectedOutputs: job.outputs,
        estimatedMs: job.estimatedMs,
        free: false,
        usage: state === 'succeeded' ? { consumeCoins: price * 10, thirdPartyConsumeMoney: price } : null,
        hidden: hiddenJobIds.has(job.jobId),
        createdAt: new Date(job.createdAt).toISOString(),
        finishedAt: state === 'succeeded' || state === 'failed' ? new Date().toISOString() : null,
      })
    }
    const want = params?.state ?? 'all'
    const filtered = out.filter((j) => {
      if (want === 'all') return true
      if (want === 'active') return j.state === 'queued' || j.state === 'running'
      if (want === 'succeeded') return j.state === 'succeeded' || j.state === 'partial'
      return j.state === 'failed'
    })
    // 可见性口径：与后端 GET /jobs?hidden= 三个取值逐字对齐
    const visibility = params?.hidden ?? 'exclude'
    const visible = filtered.filter((j) => {
      if (visibility === 'all') return true
      if (visibility === 'only') return j.hidden === true
      return j.hidden !== true
    })
    const items = visible
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, params?.limit ?? 100)
    // 「已清理总数」与本次 limit 无关（后端同样如此）：这是恢复入口上的数字，
    // 报小了就等于把记录藏起来
    return {
      items,
      hiddenCount: [...hiddenJobIds].filter((id) => jobs.has(id)).length,
    }
  },

  /**
   * mock 版「清理记录」。**只置位、不删条目** —— 与后端 `set_jobs_hidden` 同语义，
   * 不然「清空后在已清理里恢复」这条链路在 mock 下压根跑不到。
   */
  async hideJobs(jobIds) {
    await delay(120)
    let changed = 0
    for (const id of jobIds) {
      if (!id || !jobs.has(id) || hiddenJobIds.has(id)) continue
      hiddenJobIds.add(id)
      changed += 1
    }
    return changed
  },

  async unhideJobs(jobIds) {
    await delay(120)
    let changed = 0
    for (const id of jobIds) {
      if (id && hiddenJobIds.delete(id)) changed += 1
    }
    return changed
  },

  async listWorks(params) {
    await delay(200)
    const perPage = params?.perPage ?? 20
    const page = params?.page ?? 1
    const all = [...worksDb].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    let filtered = all
    if (params?.workType) filtered = filtered.filter((w) => w.workType === params.workType)
    if (params?.category && params.category !== 'all')
      filtered = filtered.filter((w) => categoryOfWork(w) === params.category)
    if (params?.origin && params.origin !== 'all')
      filtered = filtered.filter((w) => (w.origin ?? 'generated') === params.origin)
    return { items: filtered.slice((page - 1) * perPage, page * perPage), total: filtered.length }
  },

  async deleteWork(id: string) {
    await delay(140)
    const i = worksDb.findIndex((w) => w.id === id)
    if (i >= 0) worksDb.splice(i, 1)
  },

  async toggleFavorite(id: string, favorite: boolean) {
    await delay(100)
    const w = worksDb.find((x) => x.id === id)
    if (w) w.favorite = favorite
  },

  async listAssets() {
    await delay(160)
    return [...assetsDb]
  },

  async addAsset(item: AssetItem) {
    assetsDb.unshift(item)
    if (assetsDb.length > 100) assetsDb.length = 100
  },

  async removeAsset(id: string) {
    const i = assetsDb.findIndex((a) => a.id === id)
    if (i >= 0) assetsDb.splice(i, 1)
  },

  /* ---- 资产库统一视图：与 http 客户端同契约，走同一套分类规则 ---- */

  async listAssetLibrary(params) {
    await delay(220)
    const perPage = params?.perPage ?? 100
    const page = params?.page ?? 1
    const merged = mergeAssetLibrary(params)
    return {
      items: merged.slice((page - 1) * perPage, page * perPage),
      total: merged.length,
      page,
      perPage,
    }
  },

  async assetLibraryStats() {
    await delay(180)
    const all = mergeAssetLibrary({})
    return {
      total: all.length,
      works: all.filter((i) => i.origin === 'generated').length,
      assets: all.filter((i) => i.origin === 'upload').length,
      favorites: all.filter((i) => i.favorite).length,
      categories: FALLBACK_CATEGORIES.map((c) => ({
        category: c.id,
        label: c.label,
        count: all.filter((i) => i.assetCategory === c.id).length,
        generated: all.filter((i) => i.assetCategory === c.id && i.origin === 'generated').length,
        upload: all.filter((i) => i.assetCategory === c.id && i.origin === 'upload').length,
      })),
      origins: FALLBACK_ORIGINS.map((o) => ({ ...o })),
    }
  },

  async updateAssetItem(id, patch) {
    await delay(120)
    const w = worksDb.find((x) => x.id === id)
    if (w) {
      if (patch.assetCategory) w.assetCategory = patch.assetCategory
      if (patch.favorite !== undefined) w.favorite = patch.favorite
      return
    }
    const a = assetsDb.find((x) => x.id === id)
    if (a && patch.assetCategory) a.assetCategory = patch.assetCategory
  },

  async deleteAssetItem(id) {
    await delay(140)
    const wi = worksDb.findIndex((w) => w.id === id)
    if (wi >= 0) {
      worksDb.splice(wi, 1)
      return
    }
    const ai = assetsDb.findIndex((a) => a.id === id)
    if (ai >= 0) assetsDb.splice(ai, 1)
  },

  /*
   * 用户自存的视频模板。mock 版**只演示链路，不做封面的转存** ——
   * 真实后端会把上游直链拉回来转存（那是它能做、浏览器做不了的事：
   * 跨域 + 上游 CDN 的防盗链）。mock 里原样留着传进来的地址，
   * 所以这里不该被当成「封面不会过期」的证据。
   */
  async listVideoTemplates() {
    await delay(120)
    return videoTemplatesDb.map((t) => ({ ...t }))
  },

  async createVideoTemplate(req: CreateVideoTemplateRequest) {
    await delay(200)
    const now = new Date().toISOString()
    const item: CustomVideoTemplate = {
      id: `vmt_mock${Math.random().toString(36).slice(2, 10)}`,
      userId: 'mock-user',
      name: req.name,
      tag: req.tag ?? '',
      description: req.description ?? '',
      modes: req.modes?.length ? req.modes : ['short-video'],
      preset: { ...req.preset },
      storyboard: { ...req.storyboard },
      shot: req.shot ?? '',
      // mock 没有转存这一步：原样存，见上面的注释
      coverUrl: req.coverUrl ?? '',
      coverLocal: '',
      sampleUrl: req.sampleUrl ?? '',
      createdAt: now,
      updatedAt: now,
    }
    videoTemplatesDb.unshift(item)
    return { ...item }
  },

  async updateVideoTemplate(id: string, patch: UpdateVideoTemplateRequest) {
    await delay(140)
    const t = videoTemplatesDb.find((x) => x.id === id)
    if (!t) throw new ApiError('模板不存在', 'NOT_FOUND', 404)
    if (patch.name !== undefined) t.name = patch.name
    if (patch.tag !== undefined) t.tag = patch.tag
    if (patch.description !== undefined) t.description = patch.description
    /* 模板内容两项：**必须与 `createVideoTemplate` 一样真的写进去**。
       漏掉的话 mock 模式下「改完提示词再打开，还是旧的那段」——
       而真后端是对的，于是这变成一个只在 mock 下出现、又查不出原因的 bug。 */
    if (patch.storyboard !== undefined) t.storyboard = { ...patch.storyboard }
    if (patch.shot !== undefined) t.shot = patch.shot
    if (patch.coverUrl !== undefined) t.coverUrl = patch.coverUrl
    if (patch.sampleUrl !== undefined) t.sampleUrl = patch.sampleUrl
    t.updatedAt = new Date().toISOString()
    return { ...t }
  },

  async deleteVideoTemplate(id: string) {
    await delay(140)
    const i = videoTemplatesDb.findIndex((x) => x.id === id)
    if (i < 0) throw new ApiError('模板不存在', 'NOT_FOUND', 404)
    videoTemplatesDb.splice(i, 1)
  },

  /*
   * 商品资产（mock）—— 见 `productAssetsDb` 的说明：进程内数组 + 两条预置示例。
   * 与真实后端同一条账号口径（按当前登录会员过滤）。
   */
  async listProductAssets(params: ProductAssetQuery = {}) {
    await delay(160)
    return mockFilterProductAssets(params)
  },

  async productAssetFacets() {
    await delay(120)
    return mockProductAssetFacets()
  },

  async getProductAsset(assetId: string) {
    await delay(100)
    const row = productAssetsDb.find(
      (a) => a.id === assetId && a.userId === currentMockUserId(),
    )
    // 报 404 而不是 403：别人的商品与不存在的商品返回同一句话（与后端同口径）
    if (!row) throw new ApiError('商品不存在', 'NOT_FOUND', 404)
    return { ...row }
  },

  async createProductAsset(req: CreateProductAssetRequest) {
    await delay(200)
    if (!req.title?.trim()) {
      throw new ApiError('商品标题不能为空', 'TITLE_REQUIRED', 400)
    }
    const item = mockBuildProductAsset(req, 'manual')
    productAssetsDb.unshift(item)
    return { ...item }
  },

  async bulkCreateProductAssets(req: { items: CreateProductAssetRequest[] }) {
    await delay(320)
    if (!req.items.length) throw new ApiError('没有可导入的商品', 'NO_ITEMS', 400)
    // 与后端一致：**整批校验**，任何一行标题为空就整批拒绝并点名第几行。
    // mock 里放行的话，「少导入了一行」这类问题切到 http 才发现。
    req.items.forEach((it, i) => {
      if (!it.title?.trim()) {
        throw new ApiError(`第 ${i + 1} 行：商品标题不能为空`, 'TITLE_REQUIRED', 400)
      }
    })
    const created = req.items.map((it) => mockBuildProductAsset(it, 'import'))
    // 后端按 created_at DESC 返回，同一批里第 1 行在最前 —— reverse 一下对齐
    productAssetsDb.unshift(...created.slice().reverse())
    return { created: created.length, items: created }
  },

  async updateProductAsset(assetId: string, patch: UpdateProductAssetRequest) {
    await delay(180)
    const row = productAssetsDb.find(
      (a) => a.id === assetId && a.userId === currentMockUserId(),
    )
    if (!row) throw new ApiError('商品不存在', 'NOT_FOUND', 404)
    if (patch.title !== undefined) {
      if (!patch.title.trim()) throw new ApiError('商品标题不能为空', 'TITLE_REQUIRED', 400)
      row.title = patch.title.trim()
    }
    if (patch.sku !== undefined) row.sku = patch.sku
    if (patch.sellingPoints !== undefined) row.sellingPoints = patch.sellingPoints
    if (patch.language !== undefined) row.language = patch.language.trim() || 'zh-CN'
    if (patch.platform !== undefined) row.platform = patch.platform
    // 分类同样要过登记（抄后端 update 的那一步）—— 否则 mock 下改了分类名
    // 会出现「商品写着 C 类、分类条上没有 C 类」，真后端不会这样
    if (patch.category !== undefined) row.category = mockEnsureProductCategory(patch.category)
    if (patch.note !== undefined) row.note = patch.note
    if (patch.images !== undefined) {
      // 传了就是**整组替换**；封面跟着一起重算（派生只有一处）
      const images = patch.images
        .filter((i) => (i.url ?? '').trim())
        .slice(0, 30)
        .map((i) => ({
          url: i.url,
          thumbUrl: i.thumbUrl ?? '',
          localUrl: i.localUrl ?? '',
          name: i.name ?? '',
        }))
      row.images = images
      row.coverUrl = images[0]?.url ?? ''
      row.coverLocal = images[0]?.localUrl || images[0]?.thumbUrl || ''
    }
    row.updatedAt = new Date().toISOString()
    return { ...row }
  },

  async deleteProductAsset(assetId: string) {
    await delay(140)
    const i = productAssetsDb.findIndex(
      (a) => a.id === assetId && a.userId === currentMockUserId(),
    )
    if (i < 0) throw new ApiError('商品不存在', 'NOT_FOUND', 404)
    productAssetsDb.splice(i, 1)
    // ⚠️ 删商品**不动分类表**：剩下的空分类要留着（用户可能正打算往里面放货），
    // 这与后端一致 —— 后端也没有「商品删光了就顺手删分类」这回事。
  },

  /*
   * 商品分类（mock）。三条关键差异都在这里对齐真实后端，不能省：
   *   · 重名 → 409（大小写不敏感）；
   *   · 改名 → **连带改掉名下商品的 `category`**，并回 `affected`；
   *   · 删除 → 商品回落成未分类（**不删商品**），同样回 `affected`。
   * 少任何一条，mock 下都会跑通一条真后端上会失败的路径。
   */
  async listProductCategories() {
    await delay(120)
    const rows = productCategoriesDb
      .filter((c) => c.userId === currentMockUserId())
      .sort((x, y) => x.sortOrder - y.sortOrder || x.name.localeCompare(y.name))
    return { items: rows.map((c) => ({ ...c })) }
  },

  async createProductCategory(req: { name: string; sortOrder?: number }) {
    await delay(160)
    const name = (req.name ?? '').trim()
    if (!name) throw new ApiError('分类名不能为空', 'NAME_REQUIRED', 400)
    const dup = productCategoriesDb.find(
      (c) => c.userId === currentMockUserId() && c.name.toLowerCase() === name.toLowerCase(),
    )
    if (dup) throw new ApiError(`已经有一个叫「${dup.name}」的分类了`, 'DUPLICATE_CATEGORY', 409)
    const now = new Date().toISOString()
    const top = productCategoriesDb
      .filter((c) => c.userId === currentMockUserId())
      .reduce((m, c) => Math.max(m, c.sortOrder), 0)
    const item: ProductCategory = {
      id: `pc_mock_${productCategoriesDb.length + 1}`,
      userId: currentMockUserId(),
      name,
      sortOrder: req.sortOrder ?? top + 10,
      createdAt: now,
      updatedAt: now,
    }
    productCategoriesDb.push(item)
    return { ...item }
  },

  async updateProductCategory(catId: string, patch: { name?: string; sortOrder?: number }) {
    await delay(160)
    const row = productCategoriesDb.find(
      (c) => c.id === catId && c.userId === currentMockUserId(),
    )
    if (!row) throw new ApiError('分类不存在', 'NOT_FOUND', 404)
    let affected = 0
    if (patch.name !== undefined && patch.name.trim() && patch.name.trim() !== row.name) {
      const next = patch.name.trim()
      const dup = productCategoriesDb.find(
        (c) =>
          c.userId === currentMockUserId() &&
          c.id !== catId &&
          c.name.toLowerCase() === next.toLowerCase(),
      )
      if (dup) throw new ApiError(`已经有一个叫「${dup.name}」的分类了`, 'DUPLICATE_CATEGORY', 409)
      const old = row.name
      row.name = next
      // 连带改商品 —— 与后端 `rename_product_category` 同一个事务里的第二条 UPDATE
      productAssetsDb.forEach((a) => {
        if (a.userId === currentMockUserId() && a.category === old) {
          a.category = next
          affected += 1
        }
      })
      row.updatedAt = new Date().toISOString()
    }
    if (patch.sortOrder !== undefined) row.sortOrder = patch.sortOrder
    return { category: { ...row }, affected }
  },

  async deleteProductCategory(catId: string) {
    await delay(160)
    const i = productCategoriesDb.findIndex(
      (c) => c.id === catId && c.userId === currentMockUserId(),
    )
    if (i < 0) throw new ApiError('分类不存在', 'NOT_FOUND', 404)
    const [row] = productCategoriesDb.splice(i, 1)
    // 「商品还在、只是没分类了」—— 与后端一致，绝不级联删商品
    let affected = 0
    productAssetsDb.forEach((a) => {
      if (a.userId === currentMockUserId() && a.category === row.name) {
        a.category = ''
        affected += 1
      }
    })
    return { category: { ...row }, affected }
  },

  async usage(): Promise<UsageSummary> {
    await delay(240)
    const days = 14
    const r = rand('usage-seed-v1')
    const byDay = Array.from({ length: days }, (_, i) => {
      const d = new Date()
      d.setDate(d.getDate() - (days - 1 - i))
      const calls = Math.round(40 + r() * 190 * (i / days + 0.5))
      return { day: d.toISOString().slice(5, 10), calls, cost: Number((calls * (0.11 + r() * 0.09)).toFixed(2)) }
    })
    /*
     * 「按来源页面统计用量」的假数据。
     *
     * ⚠️ 这一串**不是形态/页面清单**，别拿它当第二份真源（形态真源是
     * `config/studioModes.ts`，页面清单在 `config/nav.ts`）。
     * 它演示的是**历史用量会按旧键分桶**这件事：`ai-set` 这一条对应的是
     * 「商品套图」下架前提交的那批任务 —— 后端的 `usage_records` 里真的躺着
     * 这种旧 `source_page`，所以假数据里保留它是对的（`config/pageKeys.ts`
     * 的别名表就是为把它们归回 `/detail-page` 而存在）。
     * 改形态清单时**不要顺手删这里**，那会把「旧键怎么显示」这件事从 mock 里抹掉。
     */
    const pages = [
      ['banner', 'Banner 生成'],
      ['professional/fashion', '专业服饰'],
      ['img-localize', '图片工具箱'],
      ['ai-set', '商品套图（已下架）'],
      ['detail-page', '主图与详情'],
      ['main-video', '主图视频'],
      ['copywriting', '文案工作台'],
    ] as const
    const byModel = FALLBACK_FAMILIES.filter((f) => f.kind === 'image')
      .slice(0, 6)
      .map((f, i) => ({ model: f.textSlug ?? f.id, label: f.name, calls: Math.round(180 - i * 24 + r() * 30), cost: Number((28 - i * 3.2 + r() * 4).toFixed(2)) }))
    const byPage = pages.map(([page, label], i) => ({ page, label, calls: Math.round(260 - i * 27 + r() * 40) }))

    const records: UsageSummary['records'] = []
    for (let i = 0; i < 40; i++) {
      const p = pages[Math.floor(r() * pages.length)]
      const m = FALLBACK_FAMILIES[Math.floor(r() * FALLBACK_FAMILIES.length)]
      const fail = r() < 0.06
      const d = new Date(Date.now() - i * 1000 * 60 * (17 + r() * 90))
      records.push({
        id: `rec-${1000 + i}`,
        createdAt: d.toISOString(),
        appName: p[1],
        sourcePage: p[0],
        model: m.textSlug ?? m.id,
        status: fail ? 'failed' : 'success',
        coins: fail ? 0 : Number((3 + r() * 22).toFixed(1)),
        cost: fail ? 0 : Number((0.05 + r() * 0.6).toFixed(3)),
        outputs: fail ? 0 : Math.round(1 + r() * 6),
      })
    }
    const totalCalls = byDay.reduce((s, d) => s + d.calls, 0)
    const totalCost = byDay.reduce((s, d) => s + d.cost, 0)
    return {
      totalCalls,
      totalCoins: Number((totalCalls * 6.4).toFixed(1)),
      totalCost: Number(totalCost.toFixed(2)),
      recent24h: byDay[byDay.length - 1].calls,
      successRate: 0.956,
      byDay,
      byModel,
      byPage,
      records,
    }
  },

  /* ---------------------------------------------------------------------- */
  /* 会员体系（mock）                                                        */
  /* ---------------------------------------------------------------------- */

  /**
   * **会员**登录（mock）。
   *
   * **mock 里必须能登进去**，否则整个原型演示卡在登录页上 —— 这一条与其他
   * 「mock 里做不了就抛错」的接口（如 `previewVoice`）不同：试听的价值在于
   * **真的听见那个音色**，假声音会骗人；而登录的价值在于「之后所有请求都带着
   * 某个账号」，mock 里本来就没有真实账号，所以给一批与后端种子**同名同额度**
   * 的账号（口令也是种子里那套），至少让「切账号 → 任务/额度跟着变」这件事可演示。
   *
   * 与后端种子的差别只有一处：`plan` 统一带「原型演示」标记，界面上看得出来
   * 这是假账号，不会有人拿它去对账。
   *
   * ⚠️ **只查 `mockUsers`**：后台账号 `admin` 在这个入口得到
   * 「账号或密码不正确」，与后端 `accounts.login` 逐字同口径。
   * 曾经它同时是会员表里的一行（`role: 'admin'`），所以两个入口都能登进去 ——
   * 那正是拆表要消灭的东西。
   */
  async login(account: string, password: string): Promise<LoginResponse> {
    await delay(260)
    const name = account.trim()
    const row = mockUsers.find((u) => u.account.toLowerCase() === name.toLowerCase())
    // 口令错与账号不存在返回**同一句**（与后端一致：分开就是送一个账号枚举器）
    if (!row || MOCK_PASSWORDS[row.account] !== password) {
      throw new ApiError('账号或密码不正确', 'BAD_CREDENTIALS', 401)
    }
    if (row.status !== 'active') {
      throw new ApiError('该账号已被停用，请联系管理员', 'ACCOUNT_DISABLED', 403)
    }
    mockSessionUserId = row.id
    return { token: `mock-token-${row.id}`, user: { ...row } }
  },

  async logout(): Promise<void> {
    await delay(80)
    // ⚠️ **只清会员这一侧**。顺手把 `mockSessionAdminId` 也清掉的话，
    // 「在工作台点退出，后台也跟着掉线」—— 而用户明确要的是两套独立登录态。
    mockSessionUserId = ''
  },

  /**
   * 会话校验（mock）：**没登录就抛 401**（与后端一致）。
   *
   * 这里不学后端的「放行模式」：后端那个回落是为了让 curl 与老冒烟脚本不被
   * 突然打断，属于服务端的兼容开关；界面永远要有一个明确的「我是谁」，
   * 回落成演示账号会让「登录页到底出没出现」这件事变得看不出来。
   */
  async session(): Promise<AuthUser> {
    await delay(120)
    const row = mockUsers.find((u) => u.id === mockSessionUserId)
    if (!row) throw new ApiError('登录已过期，请重新登录', 'UNAUTHORIZED', 401)
    return { ...row }
  },

  /* ---------------------- 后台侧：另一套登录（mock） ---------------------- */

  /** 后台登录（mock）：**只查 `mockAdmins`** —— 会员账号在这里「不存在」。 */
  async adminLogin(account: string, password: string): Promise<AdminLoginResponse> {
    await delay(260)
    const name = account.trim()
    const row = mockAdmins.find((a) => a.account.toLowerCase() === name.toLowerCase())
    if (!row || MOCK_ADMIN_PASSWORDS[row.account] !== password) {
      // 与会员侧同一句文案：拆表后「拿会员账号登后台」得到的就是这一句，
      // 不再有「你不是管理员」那种区分（那等于告诉对方「这个账号是存在的」）。
      throw new ApiError('账号或密码不正确', 'BAD_CREDENTIALS', 401)
    }
    if (row.status !== 'active') {
      throw new ApiError('该后台账号已被停用', 'ACCOUNT_DISABLED', 403)
    }
    mockSessionAdminId = row.id
    row.lastLoginAt = new Date().toISOString()
    return { token: `mock-admin-token-${row.id}`, admin: { ...row } }
  },

  async adminLogout(): Promise<void> {
    await delay(80)
    // 只清后台这一侧 —— 工作台不受影响（两套登录态）。
    mockSessionAdminId = ''
  },

  async adminSession(): Promise<AdminUser> {
    await delay(120)
    const row = mockAdmins.find((a) => a.id === mockSessionAdminId)
    if (!row) throw new ApiError('登录已过期，请重新登录', 'UNAUTHORIZED', 401)
    return { ...row }
  },

  async myLedger(limit = 100): Promise<CoinLedgerItem[]> {
    await delay(200)
    return mockLedger(mockSessionUserId).slice(0, limit)
  },

  async listUpstreams(): Promise<UpstreamCatalog> {
    await delay(200)
    requireMockAdmin()
    return mockCatalog()
  },

  async updateUpstream(id: string, patch: UpstreamUpdate): Promise<{ id: string; changed: string[] }> {
    await delay(260)
    requireMockAdmin()
    // ⚠️ 改的是**清单里那一份**（`mockChannelState`），不是 `mockGroups()` 现算的
    // 副本 —— 改副本的话刷新一次就全回去了，而界面上会像「保存成功了」。
    const item = mockChannelState.find((i) => i.id === id)
    if (!item) throw new ApiError(`没有这个上游：${id}`, 'UNKNOWN_PROVIDER', 404)
    const changed: string[] = []
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) continue
      // 与后端同口径：空串 = 清除覆盖（回默认）；脱敏串 = 不修改
      if (String(value).includes('••')) continue
      changed.push(key)
      if (key === 'baseUrl') item.baseUrl = String(value)
      if (key === 'model') item.model = String(value)
      if (key === 'apiKey') {
        item.apiKeyMasked = value ? maskKey(String(value)) : ''
        item.hasApiKey = Boolean(value)
      }
    }
    item.overridden = Array.from(new Set([...item.overridden, ...changed])).sort()
    return { id, changed }
  },

  /**
   * 新增一条通道。
   *
   * 与后端同一套边界：家族必须已存在（家族背后是代码，配不出来）、名称非空、
   * 报价要么单价与单位一起给要么都不给、id 不许复用（含删过的）。**新增不自动启用**。
   */
  async createUpstream(req: UpstreamCreate): Promise<UpstreamCatalogResult> {
    await delay(240)
    requireMockAdmin()
    const fam = MOCK_FAMILIES.find((f) => f.family === req.family)
    if (!fam) {
      throw new ApiError(
        `没有这个适配器家族：${req.family}（可选：${MOCK_FAMILIES.map((f) => f.family).join('、')}）`,
        'UPSTREAM_CHANNEL_INVALID',
        400,
      )
    }
    const label = (req.label || '').trim()
    if (!label) throw new ApiError('通道名称不能为空', 'UPSTREAM_CHANNEL_INVALID', 400)

    const taken = new Set([
      ...mockChannelState.map((c) => c.id),
      ...mockRemovedIds,
      ...MOCK_FAMILIES.map((f) => f.family),
    ])
    let id = (req.id || '').trim()
    if (!id) {
      let n = 2
      while (taken.has(`${req.family}-${n}`)) n += 1
      id = `${req.family}-${n}`
    }
    if (taken.has(id)) {
      throw new ApiError(`通道 id 已被占用：${id}`, 'UPSTREAM_CHANNEL_INVALID', 400)
    }
    if (req.unitPrice !== undefined || req.unit) {
      if (req.unitPrice === undefined || !req.unit || req.unitPrice <= 0) {
        throw new ApiError(
          '报价要填就单价与单位一起填（单价需大于 0）',
          'UPSTREAM_CHANNEL_INVALID',
          400,
        )
      }
    }

    mockChannelState.push({
      ...mockChannel(req.family, {
        label,
        note: fam.note,
        fields: fam.fields,
        baseUrl: '',
      }),
      id,
      // 用户新增的这条：不是代码里的默认行，覆盖值也没有
      builtin: false,
      overridden: [],
      unitPrice: req.unitPrice ?? null,
      unit: (req.unit || fam.unit || '').trim(),
    })
    return mockCatalog({ created: id })
  },

  /**
   * 删除一条通道（seed 行也能删，用「恢复默认」找回）。
   *
   * 删掉**启用中**的那条是允许的，启用态会自动落到同族剩下的第一条；
   * 但**家族最后一条**会 400 —— 删完这个家族就没有生效凭据了。
   */
  async deleteUpstream(providerId: string): Promise<UpstreamCatalogResult> {
    await delay(240)
    requireMockAdmin()
    const item = mockChannelState.find((i) => i.id === providerId)
    if (!item) throw new ApiError(`没有这个通道：${providerId}`, 'UNKNOWN_PROVIDER', 404)
    const family = item.family
    const siblings = mockChannelState.filter((c) => c.family === family && c.id !== providerId)
    if (siblings.length === 0) {
      throw new ApiError(
        `「${item.label}」是 ${family} 家族唯一的通道，删掉之后这个家族就没有生效凭据了` +
          `（走它的任务会在构造适配器时失败）。如果只是不想用它，把它的地址/Token 改掉即可，不必删。`,
        'UPSTREAM_CHANNEL_LAST',
        400,
      )
    }
    // 先记下「删的是不是启用那条」，删完就查不出来了
    const wasActive = mockActiveId(family) === providerId
    mockChannelState = mockChannelState.filter((c) => c.id !== providerId)
    mockRemovedIds.add(providerId)
    if (wasActive) mockActiveOf.set(family, siblings[0].id)
    return mockCatalog({
      id: providerId,
      removed: true,
      family,
      active: mockActiveId(family) || null,
    })
  },

  /** 把 `family` 的启用通道切到 `channel`。同族互斥由 `mockActiveOf` 保证。 */
  async activateUpstream(family: string, channel: string): Promise<UpstreamCatalogResult> {
    await delay(220)
    requireMockAdmin()
    if (!MOCK_FAMILIES.some((f) => f.family === family)) {
      throw new ApiError(`没有这个家族：${family}`, 'UNKNOWN_PROVIDER', 404)
    }
    const item = mockChannelState.find((i) => i.id === channel)
    if (!item) throw new ApiError(`没有这个通道：${channel}`, 'UNKNOWN_PROVIDER', 404)
    if (item.family !== family) {
      throw new ApiError(
        `「${item.label}」属于 ${item.family} 家族，不能挂到 ${family}`,
        'UPSTREAM_CHANNEL_MISMATCH',
        400,
      )
    }
    mockActiveOf.set(family, channel)
    return mockCatalog({ family, active: channel })
  },

  /** 恢复默认清单（清掉新增 / 删除 / 启用三类增量）。字段覆盖不动，与后端一致。 */
  async resetUpstreamChannels(): Promise<UpstreamCatalogResult> {
    await delay(240)
    requireMockAdmin()
    /*
     * ⚠️ **字段覆盖要留着**。「有哪些通道」与「这条通道填了什么」是两件事 ——
     * 后端的 `reset_channels()` 只删通道清单那个键，各行的 baseUrl / Token 覆盖
     * 存在各自的键下，不受影响。这里整体换成 seed 快照就会把它们一起清掉，
     * 而管理员点这一下时以为只是「把多出来的几条收回去」。
     */
    const prev = new Map(mockChannelState.map((c) => [c.id, c]))
    mockChannelState = structuredClone(MOCK_CHANNELS_SEED).map((seed) => {
      const old = prev.get(seed.id)
      if (!old) return seed
      return {
        ...seed,
        baseUrl: old.baseUrl,
        model: old.model,
        apiKeyMasked: old.apiKeyMasked,
        hasApiKey: old.hasApiKey,
        overridden: [...old.overridden],
      }
    })
    mockRemovedIds.clear()
    mockActiveOf.clear()
    return mockCatalog()
  },

  async adminOverview(): Promise<AdminOverview> {
    await delay(180)
    requireMockAdmin()
    return {
      counts: { jobs: 128, works: 96, users: mockUsers.length },
      users: mockUsers.length,
      activeUsers: mockUsers.filter((u) => u.status === 'active').length,
      // 后台账号**单独一格**，不与会员相加（两张表是两个概念）
      admins: mockAdmins.length,
      totalCoins: Number(mockUsers.reduce((s, u) => s + u.coins, 0).toFixed(2)),
      spentCoins: 3182.5,
    }
  },

  async listUsers(): Promise<AuthUser[]> {
    await delay(180)
    requireMockAdmin()
    return mockUsers.map((u) => ({ ...u }))
  },

  async createUser(req: CreateUserRequest): Promise<AuthUser> {
    await delay(240)
    requireMockAdmin()
    if (mockUsers.some((u) => u.account.toLowerCase() === req.account.trim().toLowerCase())) {
      throw new ApiError('该账号已存在', 'ACCOUNT_EXISTS', 400)
    }
    const now = new Date().toISOString()
    const row: AuthUser = {
      id: `u-mock-${mockUsers.length + 1}`,
      account: req.account.trim(),
      displayName: req.displayName?.trim() || req.account.trim(),
      coins: req.coins,
      plan: req.plan || '标准版 · 原型演示',
      status: 'active',
      createdAt: now,
      updatedAt: now,
    }
    mockUsers.push(row)
    MOCK_PASSWORDS[row.account] = req.password
    return { ...row }
  },

  async updateUser(userId: string, patch: UpdateUserRequest): Promise<AuthUser> {
    await delay(220)
    requireMockAdmin()
    const row = mockUsers.find((u) => u.id === userId)
    if (!row) throw new ApiError('账号不存在', 'USER_NOT_FOUND', 404)
    if (patch.displayName !== undefined) row.displayName = patch.displayName
    if (patch.plan !== undefined) row.plan = patch.plan
    if (patch.status !== undefined) row.status = patch.status
    if (patch.password) MOCK_PASSWORDS[row.account] = patch.password
    // 额度是**设为某个值**（不是增量），与后端 `UpdateUserRequest.coins` 同口径
    if (patch.coins !== undefined) row.coins = patch.coins
    row.updatedAt = new Date().toISOString()
    return { ...row }
  },

  /**
   * 删**会员**账号。
   *
   * ⚠️ 拆表后这里**没有**「不能删自己」这条了 —— 后台管理员的 id 住在
   * `mockAdmins` 里，`mockUsers` 里永远不会有它。上一版判的是
   * `mockUsers[i].id === mockSessionUserId`（会员会话！），拆表后那个条件
   * 恒为 false，看起来还在保护、实际什么都不保护。真正的自我保护
   * 搬去了 `deleteAdmin`。
   */
  async deleteUser(userId: string): Promise<{ ok: boolean }> {
    await delay(200)
    requireMockAdmin()
    const i = mockUsers.findIndex((u) => u.id === userId)
    if (i < 0) throw new ApiError('账号不存在', 'USER_NOT_FOUND', 404)
    delete MOCK_PASSWORDS[mockUsers[i].account]
    mockUsers.splice(i, 1)
    return { ok: true }
  },

  async userLedger(userId: string): Promise<CoinLedgerItem[]> {
    await delay(200)
    requireMockAdmin()
    return mockLedger(userId)
  },

  /* ---- 后台账号（管后台的人，与会员列表是两个端点） ---- */

  async listAdmins(): Promise<AdminUser[]> {
    await delay(180)
    requireMockAdmin()
    return mockAdmins.map((a) => ({ ...a }))
  },

  async createAdmin(req: CreateAdminRequest): Promise<AdminUser> {
    await delay(240)
    requireMockAdmin()
    // ⚠️ **两张表各查各的**：后台账号可以和工作台某个会员重名。
    //    这里要是顺手拿 `mockUsers` 一起查，就又把两张表连回去了。
    if (mockAdmins.some((a) => a.account.toLowerCase() === req.account.trim().toLowerCase())) {
      throw new ApiError('该后台账号已存在', 'ACCOUNT_EXISTS', 400)
    }
    const now = new Date().toISOString()
    const row: AdminUser = {
      id: `adm_mock_${mockAdmins.length + 1}`,
      account: req.account.trim(),
      displayName: req.displayName?.trim() || req.account.trim(),
      status: 'active',
      lastLoginAt: '',
      createdAt: now,
      updatedAt: now,
    }
    mockAdmins.push(row)
    MOCK_ADMIN_PASSWORDS[row.account] = req.password
    return { ...row }
  },

  async updateAdmin(adminId: string, patch: UpdateAdminRequest): Promise<AdminUser> {
    await delay(220)
    requireMockAdmin()
    const row = mockAdmins.find((a) => a.id === adminId)
    if (!row) throw new ApiError('后台账号不存在', 'ADMIN_NOT_FOUND', 404)
    // 「不能停用自己」与后端 `CANNOT_DISABLE_SELF` 同口径：正是这条把管理员锁在门外
    if (patch.status === 'disabled' && row.id === mockSessionAdminId) {
      throw new ApiError('不能停用当前登录的后台账号', 'CANNOT_DISABLE_SELF', 400)
    }
    if (patch.displayName !== undefined) row.displayName = patch.displayName
    if (patch.status !== undefined) row.status = patch.status
    if (patch.password) MOCK_ADMIN_PASSWORDS[row.account] = patch.password
    row.updatedAt = new Date().toISOString()
    return { ...row }
  },

  async deleteAdmin(adminId: string): Promise<{ ok: boolean }> {
    await delay(200)
    requireMockAdmin()
    const i = mockAdmins.findIndex((a) => a.id === adminId)
    if (i < 0) throw new ApiError('后台账号不存在', 'ADMIN_NOT_FOUND', 404)
    if (mockAdmins[i].id === mockSessionAdminId) {
      throw new ApiError('不能删除当前登录的后台账号', 'CANNOT_DELETE_SELF', 400)
    }
    // 删最后一个 = 谁也进不来后台了（与后端 `CANNOT_DELETE_LAST_ADMIN` 同口径）
    if (mockAdmins.length <= 1) {
      throw new ApiError('至少要保留一个后台账号', 'CANNOT_DELETE_LAST_ADMIN', 400)
    }
    delete MOCK_ADMIN_PASSWORDS[mockAdmins[i].account]
    mockAdmins.splice(i, 1)
    return { ok: true }
  },
}

/* ------------------------------- 工具 ------------------------------- */

function textOutputs(job: MockJob): JobOutput[] {
  return [{ url: '', type: 'text', text: job.textResult, model: job.model, taskId: job.jobId }]
}

function imageOutputs(job: MockJob): JobOutput[] {
  return Array.from({ length: job.outputs }, (_, i) => {
    // 逐张尺寸：A+ 的第 1 张 970×600、第 4 张 300×300 要各出各的比例
    const [w, h] = dimsFor(job.ratios?.[i] ?? job.aspectRatio)
    const seed = `${job.jobId}-${i}`
    return {
      url: placeholder(seed, w, h),
      thumbUrl: placeholder(seed, 400, Math.round((400 * h) / w)),
      type: 'image' as const,
      model: job.model,
      taskId: `${job.jobId}-${i}`,
    }
  })
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

function imageSize(url: string): Promise<[number, number]> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve([img.naturalWidth, img.naturalHeight])
    img.onerror = reject
    img.src = url
  })
}

/** 文案类 mock 输出：结构化、可直接看效果 */
function buildCopy(channel: string, inputs: Record<string, unknown>): string {
  const product = String(inputs.productName ?? '产品')
  const selling = String(inputs.sellingPoints ?? '核心卖点待补充')
  const audience = String(inputs.audience ?? '目标人群待补充')
  if (channel === 'xhs') {
    return [
      `【标题】${product}｜用过都说回不去了，真实体验复盘`,
      '',
      `【正文】`,
      `姐妹们，这个${product}我用了两周，说点真实的。`,
      `一开始我是被它的${selling}吸引的，实际到手之后最打动我的其实是用起来的顺手程度——`,
      `不用研究说明书，拿起来就能上手。`,
      '',
      `适合谁：${audience}`,
      `不适合谁：只想要最便宜的同类的，这个不便宜但确实值。`,
      '',
      `【话题】#${product} #好物分享 #真实测评 #生活感提升`,
      '',
      '——（原型 mock 文案，接后端后由 LLM 生成）',
    ].join('\n')
  }
  if (channel === 'douyin') {
    return [
      `【3 秒钩子】别再买错${product}了，先看这 3 点。`,
      '',
      `【口播稿】`,
      `第一，看「${selling}」——这是它跟同类拉开差距的地方。`,
      `第二，看使用场景，${audience}用起来最合适。`,
      `第三，别只看价格，算一下用一年摊下来多少钱。`,
      '',
      `【分镜提示】`,
      `0-3s：手持产品怼镜特写，快速推进`,
      `3-8s：使用过程实拍，卡点切换`,
      `8-15s：结果对比 + 产品定格，字幕打出核心卖点`,
      '',
      '——（原型 mock 文案，接后端后由 LLM 生成）',
    ].join('\n')
  }
  if (channel === 'title') {
    return [
      `${product} ${selling} ${audience.split('、')[0] ?? ''} 家用 高性价比 现货速发`,
      '',
      '【SEO 诊断】',
      '· 核心词已前置（前 12 字内）',
      '· 属性词 3 个，低于平台建议的 4-6 个，可再补 1 个材质词',
      '· 未出现违规极限词',
      '· 字符数 32 / 60，可继续补充场景词',
    ].join('\n')
  }
  return [
    `【核心卖点】`,
    `1. ${selling}`,
    `2. 适配${audience}的真实使用场景`,
    `3. 做工与用料可感知，到手即用`,
    '',
    `【详情页模块结构】`,
    `· 模块一 主视觉：一句主张 + 产品大图`,
    `· 模块二 痛点：3 个日常场景问题`,
    `· 模块三 解法：产品如何逐个对应解决`,
    `· 模块四 参数：尺寸 / 材质 / 重量 / 包装`,
    `· 模块五 信任：售后与质保说明`,
    '',
    '——（原型 mock 文案，接后端后由 LLM 生成）',
  ].join('\n')
}
